'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { sendGTMEvent } from '@next/third-parties/google';
import { faArrowLeft, faCompass, faHome, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { useEffect } from 'react';

export default function NotFound() {
  // Track 404 errors
  useEffect(() => {
    sendGTMEvent({
      event: 'page_not_found',
      page_url: window.location.href,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  }, []);

  // Popular links for users and search engines
  const popularLinks = [
    { title: 'Home', href: '/', icon: faHome },
    { title: 'Swingers Directory', href: '/directory', icon: faCompass },
    { title: 'Events', href: '/event-directory', icon: faCompass },
    { title: 'Blog', href: '/blog', icon: faCompass },
    { title: 'Features', href: '/features', icon: faCompass },
  ];

  // Location-based links for SEO
  const locationLinks = [
    { title: 'Swingers in the US', href: '/swingers/us' },
    { title: 'Swingers in Florida', href: '/swingers/us/florida' },
    { title: 'Swingers in California', href: '/swingers/us/california' },
    { title: 'Swingers in Texas', href: '/swingers/us/texas' },
    { title: 'Swingers in New York', href: '/swingers/us/new-york' },
  ];

  return (
    <div className="container flex flex-col items-center justify-center min-h-[80vh] py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-3xl bg-card/95 backdrop-blur-sm border-brand-primary/20">
        <CardHeader className="text-center">
          <CardTitle className="text-4xl font-bold text-brand-primary">404 - Page Not Found</CardTitle>
          <CardDescription className="text-xl mt-2">
            Oops! It seems you've ventured into uncharted territory.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center mb-8">
            <p className="text-lg text-muted-foreground">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-brand-primary">Popular Destinations</h3>
              <ul className="space-y-3">
                {popularLinks.map((link) => (
                  <li key={link.href}>
                    <Link 
                      href={link.href}
                      className="flex items-center text-muted-foreground hover:text-brand-primary transition-colors"
                    >
                      <FontAwesomeIcon icon={link.icon} className="mr-2 h-4 w-4" />
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4 text-brand-primary">Find Swingers Near You</h3>
              <ul className="space-y-3">
                {locationLinks.map((link) => (
                  <li key={link.href}>
                    <Link 
                      href={link.href}
                      className="flex items-center text-muted-foreground hover:text-brand-primary transition-colors"
                    >
                      <FontAwesomeIcon icon={faCompass} className="mr-2 h-4 w-4" />
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg mt-6">
            <h3 className="text-lg font-semibold mb-2">Looking for something specific?</h3>
            <div className="flex gap-4 flex-wrap">
              <Link href="/search">
                <Button variant="outline" className="gap-2">
                  <FontAwesomeIcon icon={faSearch} className="h-4 w-4" />
                  Search Swingular
                </Button>
              </Link>
              <Link href="/contact">
                <Button variant="outline" className="gap-2">
                  Report This Issue
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-center pt-2 pb-6">
          <Link href="/">
            <Button className="gap-2">
              <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
              Return to Homepage
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
